import { groundRing, slash, star, sword, taiji } from '../kit';
import { C, Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กระบี่วงธารา (ยอดหมอก, taiji): the mist-peak sword that never stops circling. A slate-hilted jian with a
 * pale mist tassel, water rings in cool blue-grey, the taiji disc, the seven stars as cold points.
 */

const HILT = '#5a6a88';
const TASSEL = '#9fb4cc';
const WATER = '#7aa8c8';
const WATER_HI = '#d8ecf8';
const INK = '#26283a';

const mistSword = (p: Pix, x0: number, y0: number, x1: number, y1: number) =>
  sword(p, x0, y0, x1, y1, { hilt: HILT, tassel: TASSEL });

/** A flat ripple ellipse pair (water). */
function ripple(p: Pix, cx: number, cy: number, rx: number): void {
  p.ring(cx, cy, rx, rx * 0.32, WATER);
  p.ring(cx, cy - 0.5, rx * 0.6, rx * 0.2, WATER_HI);
}

registerIcons('skill', {
  // The sword draws an unbroken ring of water.
  'ts-circle': (p) => {
    p.ring(16, 16, 12, 12, WATER, 0, Math.PI * 2);
    p.ring(16, 16, 12, 12, WATER_HI, -Math.PI * 0.9, -Math.PI * 0.2);
    mistSword(p, 6, 26, 26, 6);
  },
  // Yin and yang made one and split down the middle: the disc parts round a falling blade.
  'ts-yinyang': (p) => {
    const t = new Pix(32, 32);
    taiji(t, 16, 13, 9, WATER_HI, INK);
    for (let y = 0; y < 32; y++)
      for (let x = 0; x < 32; x++) {
        const v = t.d[y * 32 + x]!;
        const nx = x < 16 ? x - 3 : x + 3;
        if (v >>> 24 && nx >= 0 && nx < 32) p.d[y * 32 + nx] = v;
      }
    mistSword(p, 16, 1, 16, 28);
  },
  // Seven points of sword qi along the northern dipper, the sword pointing them out.
  'ts-stars': (p) => {
    mistSword(p, 3, 29, 15, 17);
    const dipper = [
      [16, 14],
      [19, 11],
      [22, 12],
      [24, 9],
      [24, 4],
      [28, 3],
      [29, 8],
    ] as const;
    p.path(dipper.slice(3), WATER, 1);
    p.path(dipper.slice(0, 4), WATER, 1);
    dipper.forEach(([x, y], k) => star(p, x, y, k === 0 ? 2 : 1, WATER_HI));
  },
  // Stillness: the sword stands point-down in water that does not move.
  'ts-still': (p) => {
    ripple(p, 17, 24, 12);
    mistSword(p, 17, 4, 17, 25);
    p.rect(9, 27, 16, 1, WATER);
  },
  // Two cuts that flow into one another like a stream.
  'ts-flow': (p) => {
    slash(p, 12, 12, 9, Math.PI * 0.1, Math.PI * 1.1, WATER, 3);
    slash(p, 20, 20, 9, -Math.PI * 0.9, Math.PI * 0.1, WATER, 3);
    mistSword(p, 5, 28, 27, 4);
  },
  // The sword goes straight as a thread through everything in its way.
  'ts-thread': (p) => {
    p.rect(2, 16, 28, 1, WATER_HI);
    for (const x of [9, 17]) {
      p.rect(x, 11, 2, 3, WATER).rect(x - 1, 14, 4, 5, WATER).rect(x, 19, 1, 4, WATER).rect(x + 2, 19, 1, 4, WATER);
    }
    mistSword(p, 1, 23, 30, 23);
  },
  // A still mind: the sword upright before a calm full ring.
  'ts-focus': (p) => {
    p.ring(16, 15, 10, 10, WATER_HI, 0, Math.PI * 2);
    p.ring(16, 15, 8, 8, WATER, 0, Math.PI * 2);
    mistSword(p, 16, 29, 16, 3);
  },
  // Three ringing cuts over a chosen circle, the taiji sealing it from above.
  'ts-heaven': (p) => {
    groundRing(p, 16, 23, 12, 5, WATER);
    for (const r of [6, 9, 12]) slash(p, 16, 23, r, -Math.PI * 0.95, -Math.PI * 0.05, WATER_HI, 2);
    taiji(p, 16, 8, 5, WATER_HI, INK);
    p.px(5, 5, C.white).px(26, 5, C.white);
  },
});
